<template>
  <transition name="fade" mode="out-in">
    <button type="button" @click="onClick($event)" :class="[buttonClass, activeButtonClass]" :key="isAnimating">
        <i v-if="icon"
          :class="[activeIcon, text && 'with-text', 'icon-fx', isAnimating && 'icon-fx-on']"
          class="fa" />
        <span v-html="activeText" />
      <slot />
    </button>
  </transition>
</template>

<script>
/**
 * A fancy-pants animated button 🧐
 * By default: looks identical to a LinkButton, but on click it'll briefly cross-fade into a different icon/text/style.
 *
 * You can also trigger this animation whenever you want via the animate-method.
 * This makes it possible to give the button multiple different-looking animations. (e.g. for success/error/in progress states)
 */
export default {
  name: 'animatedButton',
  props: {
    icon: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: ''
    },
    buttonClass: { // To control the button's style; by default it looks like a LinkButton
      type: String,
      default: 'btn link-button'
    },
    animatedIcon: { // On click, the button's icon briefly changes into this one
      type: String,
      default: 'fa-check t-green'
    },
    animatedText: { // On click, the button's text briefly changes into this
      type: String,
      default: ''
    },
    animatedButtonClass: { // On click, the button will briefly have these additional classes (e.g. to change its color, border, etc)
      type: String,
      default: ''
    },
    animationDurationMs: { // On click, the button changes its look for this many ms, before it fades back to normal
      type: Number,
      default: 1600
    },
    animateOnClick: { // Only set this to false if you want to manually control the animations via the animate()-method (e.g. so you can have multiple different animations)
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isAnimating: false,
      activeIcon: this.icon,
      activeText: this.text,
      activeButtonClass: ''
    }
  },
  methods: {
    onClick (event) {
      if (this.animateOnClick) {
        this.animate(
          this.animatedButtonClass,
          this.animatedText ? this.animatedText : this.text,
          this.animatedIcon)
      }
      this.$emit('click', event)
    },
    animate (classes, text, icon) {
      if (this.isAnimating) {
        return // Ignore if user is clicking too fast
      }

      const tmpText = this.activeText
      const tmpIcon = this.activeIcon

      this.isAnimating = true
      this.activeText = text
      this.activeIcon = icon
      this.activeButtonClass = classes
      setTimeout(() => {
        this.isAnimating = false
        this.activeText = tmpText
        this.activeIcon = tmpIcon
        this.activeButtonClass = ''
      }, this.animationDurationMs)
    }
  }
}
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_loaders/_colors.scss";

.icon-fx {
  transition: color 0.3s;

  &.icon-fx-on {
    color: $green;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0.25;
}
</style>
